import { SxProps, Theme } from "@mui/material";
import viewSignRequestTokens, {
  viewSignRequestCompactTokens,
  viewSignRequestStatusTokens,
  ViewSignRequestStatusKey,
} from "./ViewSignRequest.tokens";

const {
  card,
  outlined,
  page,
  breadcrumb,
  header,
  statusChip,
  primaryButton,
  secondaryButton,
  menu,
  progress,
  banner,
  sectionHeader,
  documentRow,
  levelHeader,
  recipientRow,
  message,
  audit,
  details,
} = viewSignRequestTokens;

const compact = viewSignRequestCompactTokens;

/**
 * The reference's 375px column. `xs` starts at 0, so the narrow phone cannot be
 * a breakpoint key — it has to ride in as a trailing raw media query, which the
 * cascade lets win under 390px because `rs` always emits it last.
 */
const PHONE_SM = "@media (max-width:389.98px)";

/** `[phone 375, phone 390, tablet 834, desktop]` for one CSS property. */
type Tiers = readonly [any, any, any, any];

/**
 * Expands a map of `Tiers` into one sx block: the MUI breakpoint object for
 * 390/834/desktop plus a single `PHONE_SM` override carrying everything the
 * 375 column draws differently.
 *
 * Call it **once** per style object — a second spread would replace the first
 * one's `PHONE_SM` key rather than merge into it.
 */
const rs = (spec: Record<string, Tiers>) => {
  const base: Record<string, any> = {};
  const small: Record<string, any> = {};

  Object.entries(spec).forEach(([prop, [phoneSm, phone, tablet, desktop]]) => {
    base[prop] = { xs: phone, sm: tablet, md: desktop };
    if (phoneSm !== phone) small[prop] = phoneSm;
  });

  return Object.keys(small).length ? { ...base, [PHONE_SM]: small } : base;
};

/** Pulls one property's four tiers out of a compact triple + its desktop value. */
const t = (triple: readonly any[], desktop: any): Tiers =>
  [triple[0], triple[1], triple[2], desktop] as const;

/** White rounded card chrome shared by every block on the screen. */
const cardChrome = {
  bgcolor: card.bg,
  border: `1px solid ${card.border}`,
  borderRadius: card.radius,
  boxShadow: card.shadow,
} as const;

/**
 * One row of the kebab dropdown. `minHeight`/`lineHeight` are pinned because
 * MUI's own MenuItem defaults (36px tall, inherited line-height) would otherwise
 * decide the row height instead of the design's 10px/13px padding.
 */
const menuItemChrome = {
  display: "flex",
  alignItems: "center",
  gap: menu.itemGap,
  p: menu.itemPadding,
  minHeight: "unset",
  borderRadius: menu.itemRadius,
  fontSize: menu.itemFontSize,
  fontWeight: menu.itemFontWeight,
  lineHeight: "normal",
  color: menu.itemColor,
  "&:hover, &.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover": {
    bgcolor: menu.itemHoverBg,
  },
  "&.Mui-disabled": {
    opacity: 1,
    color: menu.disabledColor,
  },
} as const;

/** Outlined pill used by Send Reminder, Preview and the audit footer. */
const outlinedChrome = {
  bgcolor: outlined.bg,
  border: `1px solid ${outlined.border}`,
  color: outlined.color,
  textTransform: "none",
  lineHeight: "normal",
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": {
    bgcolor: outlined.bg,
    borderColor: outlined.hoverBorder,
    color: outlined.hoverColor,
    boxShadow: "none",
  },
} as const;

/** Radius every white card steps through — 15 / 15 / 16 / 16. */
const cardRadiusTiers = t(compact.cardRadius, card.radius);

const viewSignRequestStyles = {
  /**
   * Below `md` the reference runs the sticky bar edge to edge, so the page has
   * to climb back out of `CenteredAuthenticatedLayout`'s top margin and
   * `Container`'s side gutters before laying its own padding down.
   */
  page: {
    ...rs({
      marginTop: t(compact.page.layoutTopGutter, 0),
      marginLeft: t(compact.page.layoutSideGutter, 0),
      marginRight: t(compact.page.layoutSideGutter, 0),
      /** The bar owns the top edge below md; the header card adds the inset. */
      paddingTop: [0, 0, 0, page.paddingTop],
      paddingLeft: t(compact.page.padX, page.paddingX),
      paddingRight: t(compact.page.padX, page.paddingX),
      paddingBottom: t(compact.page.padBottom, page.paddingBottom),
    }),
  } as SxProps<Theme>,

  /**
   * Desktop: cards + a fixed right rail. Tablet: two even columns. Phone: one.
   */
  columns: {
    display: "grid",
    ...rs({
      gridTemplateColumns: [
        "minmax(0,1fr)",
        "minmax(0,1fr)",
        "minmax(0,1fr) minmax(0,1fr)",
        page.gridTemplateColumns,
      ],
      gap: t(compact.page.gap, page.columnGap),
    }),
    alignItems: "start",
  } as SxProps<Theme>,

  column: {
    display: "flex",
    flexDirection: "column",
    ...rs({ gap: t(compact.page.gap, page.sectionGap) }),
    minWidth: 0,
  } as SxProps<Theme>,

  // ------------------------------------------------------------- sticky bar

  /**
   * "← Sign Requests ⋮" strip that stands in for the breadcrumb below `md`.
   * Full bleed, so it cancels the page's own side padding, and sticky under the
   * app's 64px TopNav.
   */
  pageBar: {
    position: "sticky",
    top: compact.bar.stickyTop,
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    bgcolor: card.bg,
    borderBottom: `1px solid ${compact.bar.borderColor}`,
    ...rs({
      height: t(compact.bar.height, "auto"),
      gap: t(compact.bar.gap, "0px"),
      marginLeft: [
        `-${compact.page.padX[0]}`,
        `-${compact.page.padX[1]}`,
        `-${compact.page.padX[2]}`,
        0,
      ],
      marginRight: [
        `-${compact.page.padX[0]}`,
        `-${compact.page.padX[1]}`,
        `-${compact.page.padX[2]}`,
        0,
      ],
      paddingTop: t(compact.bar.padY, 0),
      paddingBottom: t(compact.bar.padY, 0),
      paddingLeft: t(compact.bar.padX, 0),
      paddingRight: t(compact.bar.padX, 0),
    }),
  } as SxProps<Theme>,

  pageBarBack: {
    width: compact.bar.buttonSize,
    height: compact.bar.buttonSize,
    p: 0,
    borderRadius: compact.bar.buttonRadius,
    color: compact.bar.titleColor,
    flexShrink: 0,
    ...rs({ marginLeft: t(compact.bar.backOffset, 0) }),
    "&:hover": { bgcolor: compact.bar.backHoverBg },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  pageBarTitle: {
    ...rs({ fontSize: t(compact.bar.titleFontSize, compact.bar.titleFontSize[2]) }),
    fontWeight: compact.bar.titleFontWeight,
    color: compact.bar.titleColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Tablet keeps the second crumb; the phone bar stops at "Sign Requests". */
  pageBarCurrent: {
    fontSize: compact.bar.titleFontSize[2],
    fontWeight: compact.bar.titleFontWeight,
    color: compact.bar.titleColor,
    lineHeight: "normal",
    maxWidth: compact.bar.currentMaxWidth,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  pageBarSeparator: {
    width: compact.bar.separatorSize,
    height: compact.bar.separatorSize,
    color: compact.bar.separatorColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  pageBarKebab: {
    width: compact.bar.buttonSize,
    height: compact.bar.buttonSize,
    p: 0,
    ml: "auto",
    borderRadius: compact.bar.buttonRadius,
    color: compact.bar.kebabColor,
    flexShrink: 0,
    "&:hover": {
      bgcolor: compact.bar.kebabHoverBg,
      color: compact.bar.kebabHoverColor,
    },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  // ---------------------------------------------------------------- breadcrumb

  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: breadcrumb.gap,
    mb: breadcrumb.marginBottom,
    minWidth: 0,
  } as SxProps<Theme>,

  breadcrumbLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    p: 0,
    minWidth: 0,
    border: "none",
    background: "none",
    fontSize: breadcrumb.fontSize,
    fontWeight: breadcrumb.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    color: breadcrumb.linkColor,
    "&:hover": {
      background: "none",
      color: breadcrumb.linkHoverColor,
      textDecoration: "none",
    },
  } as SxProps<Theme>,

  breadcrumbSeparator: {
    width: breadcrumb.separatorSize,
    height: breadcrumb.separatorSize,
    color: breadcrumb.separatorColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  breadcrumbCurrent: {
    fontSize: breadcrumb.fontSize,
    fontWeight: breadcrumb.activeFontWeight,
    lineHeight: "normal",
    color: breadcrumb.activeColor,
    maxWidth: breadcrumb.activeMaxWidth,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  // -------------------------------------------------------------- header card

  headerCard: {
    ...cardChrome,
    ...rs({
      borderRadius: cardRadiusTiers,
      padding: t(compact.header.padding, header.padding),
      marginTop: t(compact.header.marginTop, 0),
      marginBottom: t(compact.header.marginBottom, header.marginBottom),
    }),
  } as SxProps<Theme>,

  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    ...rs({ gap: t(compact.header.rowGap, header.rowGap) }),
  } as SxProps<Theme>,

  headerIconTile: (key: ViewSignRequestStatusKey) =>
    ({
      ...rs({
        width: t(compact.header.iconTileSize, header.iconTileSize),
        height: t(compact.header.iconTileSize, header.iconTileSize),
        borderRadius: t(compact.header.iconTileRadius, header.iconTileRadius),
      }),
      bgcolor: viewSignRequestStatusTokens[key].bg,
      color: viewSignRequestStatusTokens[key].dot,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }) as SxProps<Theme>,

  headerIconGlyph: {
    ...rs({
      width: t(compact.header.iconGlyphSize, header.iconGlyphSize),
      height: t(compact.header.iconGlyphSize, header.iconGlyphSize),
    }),
    flexShrink: 0,
  } as SxProps<Theme>,

  headerTitleRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: header.titleGap,
    minWidth: 0,
  } as SxProps<Theme>,

  headerTitle: {
    m: 0,
    ...rs({
      fontSize: t(compact.header.titleFontSize, header.titleFontSize),
      lineHeight: t(compact.header.titleLineHeight, "normal"),
    }),
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    color: header.titleColor,
    textWrap: "pretty",
  } as SxProps<Theme>,

  /** Phone drops the status chip onto its own line under the title block. */
  headerChipRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: header.titleGap,
    ...rs({ marginTop: t(compact.header.chipMarginTop, 0) }),
  } as SxProps<Theme>,

  headerMetaRow: {
    display: "flex",
    ...rs({
      flexDirection: ["column", "column", "row", "row"],
      alignItems: ["flex-start", "flex-start", "center", "center"],
      gap: t(compact.header.metaGap, header.metaGap),
      marginTop: t(compact.header.metaMarginTop, header.metaMarginTop),
    }),
    flexWrap: "wrap",
  } as SxProps<Theme>,

  headerMetaItem: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    ...rs({ fontSize: t(compact.header.metaFontSize, header.metaFontSize) }),
    color: header.metaColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** The compact reference writes the meta line without its leading glyphs. */
  headerMetaIcon: {
    display: { xs: "none", md: "block" },
    width: header.metaIconSize,
    height: header.metaIconSize,
    color: header.metaIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  headerMetaStrong: {
    color: header.metaStrongColor,
    fontWeight: header.metaStrongWeight,
  } as SxProps<Theme>,

  headerActions: {
    display: "flex",
    ...rs({
      flexDirection: ["column", "column", "row", "row"],
      alignItems: ["stretch", "stretch", "center", "center"],
      gap: t(compact.header.actionsGap, header.actionsGap),
      marginTop: t(compact.header.actionsMarginTop, 0),
    }),
    flexShrink: 0,
  } as SxProps<Theme>,

  // ------------------------------------------------------------ status chips

  statusChip: (key: ViewSignRequestStatusKey, row = false) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: statusChip.gap,
      ...rs({
        padding: row
          ? t(compact.statusChip.rowPadding, statusChip.rowPadding)
          : t(compact.statusChip.padding, statusChip.padding),
      }),
      borderRadius: statusChip.radius,
      bgcolor: viewSignRequestStatusTokens[key].bg,
      flexShrink: 0,
      maxWidth: "100%",
    }) as SxProps<Theme>,

  statusDot: (key: ViewSignRequestStatusKey, row = false) =>
    ({
      ...rs({
        width: row
          ? t(compact.statusChip.rowDotSize, statusChip.dotSize)
          : t(compact.statusChip.dotSize, statusChip.dotSize),
        height: row
          ? t(compact.statusChip.rowDotSize, statusChip.dotSize)
          : t(compact.statusChip.dotSize, statusChip.dotSize),
      }),
      borderRadius: "50%",
      bgcolor: viewSignRequestStatusTokens[key].dot,
      flexShrink: 0,
    }) as SxProps<Theme>,

  statusLabel: (key: ViewSignRequestStatusKey, row = false) =>
    ({
      ...rs({
        fontSize: row
          ? t(compact.statusChip.rowFontSize, statusChip.fontSize)
          : t(compact.statusChip.fontSize, statusChip.fontSize),
      }),
      fontWeight: statusChip.fontWeight,
      lineHeight: "normal",
      color: viewSignRequestStatusTokens[key].color,
      whiteSpace: "nowrap",
    }) as SxProps<Theme>,

  // ---------------------------------------------------------- header buttons

  primaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: primaryButton.gap,
    minWidth: 0,
    ...rs({
      height: t(compact.header.buttonHeight, primaryButton.height),
      padding: t(compact.header.primaryPadding, primaryButton.padding),
      fontSize: t(compact.header.buttonFontSize, primaryButton.fontSize),
      width: ["100%", "100%", "auto", "auto"],
    }),
    background: primaryButton.bg,
    borderRadius: primaryButton.radius,
    color: primaryButton.color,
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: primaryButton.hoverBg, boxShadow: "none" },
    "&.Mui-disabled": { background: primaryButton.bg, color: primaryButton.color, opacity: 0.6 },
  } as SxProps<Theme>,

  secondaryButton: {
    ...outlinedChrome,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: secondaryButton.gap,
    minWidth: 0,
    ...rs({
      height: t(compact.header.buttonHeight, secondaryButton.height),
      padding: t(compact.header.secondaryPadding, secondaryButton.padding),
      fontSize: t(compact.header.buttonFontSize, secondaryButton.fontSize),
      width: ["100%", "100%", "auto", "auto"],
    }),
    borderRadius: secondaryButton.radius,
    fontWeight: secondaryButton.fontWeight,
  } as SxProps<Theme>,

  /** Tooltip wrapper around a header button — has to stretch when they stack. */
  headerActionSlot: {
    display: { xs: "block", sm: "inline-flex" },
    minWidth: 0,
  } as SxProps<Theme>,

  /** Leading glyph inside Send Reminder / Download. */
  headerButtonIcon: {
    ...rs({
      width: t(compact.header.buttonIconSize, primaryButton.iconSize),
      height: t(compact.header.buttonIconSize, primaryButton.iconSize),
    }),
    flexShrink: 0,
  } as SxProps<Theme>,

  headerKebab: {
    ...outlinedChrome,
    width: secondaryButton.kebabSize,
    height: secondaryButton.kebabSize,
    p: 0,
    borderRadius: secondaryButton.radius,
    color: secondaryButton.kebabColor,
    flexShrink: 0,
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  // ------------------------------------------------------------- kebab menu

  menuPaper: {
    bgcolor: menu.paperBg,
    border: `1px solid ${menu.paperBorder}`,
    borderRadius: menu.paperRadius,
    boxShadow: menu.paperShadow,
    p: menu.paperPadding,
    minWidth: menu.paperMinWidth,
    mt: menu.paperOffsetTop,
  } as SxProps<Theme>,

  /** The paper already carries the design's 6px inset. */
  menuList: { py: 0 } as SxProps<Theme>,

  menuItem: { ...menuItemChrome } as SxProps<Theme>,

  menuItemLocked: {
    ...menuItemChrome,
    "&.Mui-disabled": {
      ...menuItemChrome["&.Mui-disabled"],
      pointerEvents: "auto",
      cursor: "default",
      "&:hover": { bgcolor: "transparent" },
    },
  } as SxProps<Theme>,

  menuItemDestructive: {
    ...menuItemChrome,
    color: menu.destructiveColor,
  } as SxProps<Theme>,

  /** Trailing lock on a feature-gated row. */
  menuItemLock: {
    ml: "auto",
    fontSize: menu.lockIconSize,
    color: menu.disabledColor,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- progress

  progressStrip: {
    display: "flex",
    alignItems: "center",
    ...rs({
      gap: t(compact.progress.gap, progress.gap),
      marginTop: t(compact.progress.marginTop, progress.marginTop),
      paddingTop: t(compact.progress.paddingTop, progress.paddingTop),
    }),
    borderTop: `1px solid ${card.divider}`,
  } as SxProps<Theme>,

  progressLabel: {
    ...rs({
      fontSize: t(compact.progress.labelFontSize, progress.labelFontSize),
    }),
    fontWeight: progress.labelFontWeight,
    letterSpacing: progress.labelLetterSpacing,
    color: progress.labelColor,
    lineHeight: "normal",
    flexShrink: 0,
  } as SxProps<Theme>,

  progressTrack: {
    flex: 1,
    minWidth: 0,
    ...rs({
      height: t(compact.progress.barHeight, progress.barHeight),
      borderRadius: t(compact.progress.barRadius, progress.barRadius),
    }),
    bgcolor: progress.barTrack,
    overflow: "hidden",
  } as SxProps<Theme>,

  progressFill: (key: ViewSignRequestStatusKey, pct: number) =>
    ({
      height: "100%",
      width: `${pct}%`,
      ...rs({
        borderRadius: t(compact.progress.barRadius, progress.barRadius),
      }),
      bgcolor: viewSignRequestStatusTokens[key].dot,
    }) as SxProps<Theme>,

  progressValue: {
    ...rs({
      fontSize: t(compact.progress.valueFontSize, progress.valueFontSize),
    }),
    fontWeight: progress.valueFontWeight,
    color: progress.valueColor,
    lineHeight: "normal",
    flexShrink: 0,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  // ---------------------------------------------------------------- banner

  /** Phone stacks the copy above a full-width action; ≥sm keeps one row. */
  banner: (isError: boolean) =>
    ({
      display: "flex",
      ...rs({
        flexDirection: t(compact.banner.direction, "row"),
        alignItems: ["stretch", "stretch", "center", "center"],
        gap: t(compact.banner.gap, banner.gap),
        padding: t(compact.banner.padding, banner.padding),
        marginBottom: t(compact.banner.marginBottom, banner.marginBottom),
      }),
      borderRadius: banner.radius,
      bgcolor: isError ? banner.errorBg : banner.successBg,
      border: `1px solid ${isError ? banner.errorBorder : banner.successBorder}`,
    }) as SxProps<Theme>,

  /** Icon + copy, which the phone keeps side by side inside the stacked card. */
  bannerLead: {
    display: "flex",
    ...rs({
      alignItems: ["flex-start", "flex-start", "center", "center"],
      gap: t(compact.banner.innerGap, banner.gap),
    }),
    flex: 1,
    minWidth: 0,
  } as SxProps<Theme>,

  bannerIcon: {
    ...rs({
      width: t(compact.banner.iconCircleSize, banner.iconCircleSize),
      height: t(compact.banner.iconCircleSize, banner.iconCircleSize),
    }),
    borderRadius: "50%",
    bgcolor: card.bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  bannerTitle: (isError: boolean) =>
    ({
      ...rs({
        fontSize: t(compact.banner.titleFontSize, banner.titleFontSize),
      }),
      fontWeight: banner.titleFontWeight,
      lineHeight: "normal",
      color: isError ? banner.errorColor : banner.successColor,
    }) as SxProps<Theme>,

  bannerSub: {
    ...rs({
      marginTop: t(compact.banner.subMarginTop, banner.subMarginTop),
      fontSize: t(compact.banner.subFontSize, banner.subFontSize),
      lineHeight: t(compact.banner.subLineHeight, "normal"),
    }),
    color: banner.subColor,
  } as SxProps<Theme>,

  bannerButton: {
    ...outlinedChrome,
    minWidth: 0,
    ...rs({
      height: t(compact.banner.buttonHeight, banner.buttonHeight),
      padding: t(compact.banner.buttonPadding, banner.buttonPadding),
      borderRadius: t(compact.banner.buttonRadius, banner.buttonRadius),
      fontSize: t(compact.banner.buttonFontSize, banner.buttonFontSize),
      width: t(compact.banner.buttonWidth, "auto"),
    }),
    borderColor: banner.successBorder,
    color: banner.successColor,
    fontWeight: banner.buttonFontWeight,
    flexShrink: 0,
    "&:hover": {
      bgcolor: outlined.bg,
      borderColor: banner.successHoverBorder,
      color: banner.successColor,
      boxShadow: "none",
    },
  } as SxProps<Theme>,

  // ------------------------------------------------------------------ cards

  card: {
    ...cardChrome,
    ...rs({ borderRadius: cardRadiusTiers }),
    overflow: "hidden",
  } as SxProps<Theme>,

  /**
   * `divided` is false for the Signers header below `md`, where the reference
   * drops the rule so the order badge can sit flush under the title.
   */
  sectionHeader: (divided = true) =>
    ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      ...rs({
        gap: t(compact.sectionHeader.gap, sectionHeader.gap),
        padding: t(compact.sectionHeader.padding, sectionHeader.padding),
        borderBottom: divided
          ? [
              `1px solid ${card.divider}`,
              `1px solid ${card.divider}`,
              `1px solid ${card.divider}`,
              `1px solid ${card.divider}`,
            ]
          : ["none", "none", "none", `1px solid ${card.divider}`],
      }),
    }) as SxProps<Theme>,

  sectionHeaderLead: {
    display: "flex",
    alignItems: "center",
    ...rs({ gap: t(compact.sectionHeader.gap, sectionHeader.gap) }),
    minWidth: 0,
  } as SxProps<Theme>,

  sectionTitle: {
    ...rs({
      fontSize: t(compact.sectionHeader.titleFontSize, sectionHeader.titleFontSize),
    }),
    fontWeight: sectionHeader.titleFontWeight,
    color: sectionHeader.titleColor,
    lineHeight: "normal",
    m: 0,
  } as SxProps<Theme>,

  sectionCount: (approver = false) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      ...rs({
        minWidth: t(compact.sectionHeader.countMinWidth, sectionHeader.countMinWidth),
        height: t(compact.sectionHeader.countHeight, sectionHeader.countHeight),
        padding: t(compact.sectionHeader.countPadding, sectionHeader.countPadding),
        borderRadius: t(compact.sectionHeader.countRadius, sectionHeader.countRadius),
        fontSize: t(compact.sectionHeader.countFontSize, sectionHeader.countFontSize),
      }),
      bgcolor: approver
        ? sectionHeader.countApproverBg
        : sectionHeader.countBg,
      color: approver
        ? sectionHeader.countApproverColor
        : sectionHeader.countColor,
      fontWeight: sectionHeader.countFontWeight,
      lineHeight: "normal",
      flexShrink: 0,
    }) as SxProps<Theme>,

  /** Desktop keeps the badge inside the header row; below `md` it is hidden. */
  sectionBadge: {
    display: { xs: "none", md: "flex" },
    alignItems: "center",
    gap: sectionHeader.badgeGap,
    fontSize: sectionHeader.badgeFontSize,
    fontWeight: sectionHeader.badgeFontWeight,
    color: sectionHeader.badgeColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /**
   * The compact reference re-lays the same badge as a plain caption row under
   * the Signers header, with no leading glyph.
   */
  sectionBadgeRow: {
    display: { xs: "block", md: "none" },
    ...rs({
      padding: t(compact.orderBadge.padding, 0),
      fontSize: t(compact.orderBadge.fontSize, sectionHeader.badgeFontSize),
    }),
    fontWeight: sectionHeader.badgeFontWeight,
    color: sectionHeader.badgeColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  sectionBadgeIcon: {
    width: sectionHeader.badgeIconSize,
    height: sectionHeader.badgeIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  sectionHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  } as SxProps<Theme>,

  sectionToggle: (expanded: boolean) =>
    ({
      ...rs({
        width: t(compact.sectionHeader.toggleSize, sectionHeader.toggleSize),
        height: t(compact.sectionHeader.toggleSize, sectionHeader.toggleSize),
      }),
      p: 0,
      borderRadius: sectionHeader.toggleRadius,
      color: sectionHeader.toggleColor,
      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform .2s",
      "&:hover": {
        bgcolor: sectionHeader.toggleHoverBg,
        color: sectionHeader.toggleHoverColor,
      },
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "-2px",
      },
    }) as SxProps<Theme>,

  sectionToggleGlyph: {
    ...rs({
      width: t(compact.sectionHeader.toggleIconSize, sectionHeader.toggleIconSize),
      height: t(compact.sectionHeader.toggleIconSize, sectionHeader.toggleIconSize),
    }),
  } as SxProps<Theme>,

  // -------------------------------------------------------------- documents

  /**
   * The compact reference hangs the rule on each row's top edge instead of its
   * bottom, so the list never doubles a line onto the card's own border.
   */
  documentRow: {
    display: "flex",
    alignItems: "center",
    ...rs({
      gap: t(compact.documentRow.gap, documentRow.gap),
      padding: t(compact.documentRow.padding, documentRow.padding),
      borderTop: [
        `1px solid ${card.divider}`,
        `1px solid ${card.divider}`,
        `1px solid ${card.divider}`,
        "none",
      ],
      borderBottom: [
        "none",
        "none",
        "none",
        `1px solid ${card.divider}`,
      ],
    }),
    cursor: "pointer",
    "&:hover": { bgcolor: card.hoverBg },
  } as SxProps<Theme>,

  documentThumb: {
    ...rs({
      width: t(compact.documentRow.thumbWidth, documentRow.thumbWidth),
      height: t(compact.documentRow.thumbHeight, documentRow.thumbHeight),
      borderRadius: t(compact.documentRow.thumbRadius, documentRow.thumbRadius),
    }),
    bgcolor: documentRow.thumbBg,
    border: `1px solid ${documentRow.thumbBorder}`,
    color: documentRow.thumbGlyphColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  documentName: {
    ...rs({
      fontSize: t(compact.documentRow.nameFontSize, documentRow.nameFontSize),
    }),
    fontWeight: documentRow.nameFontWeight,
    color: documentRow.nameColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  documentMeta: {
    ...rs({
      marginTop: t(compact.documentRow.metaMarginTop, documentRow.metaMarginTop),
      fontSize: t(compact.documentRow.metaFontSize, documentRow.metaFontSize),
    }),
    color: documentRow.metaColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** Explains why a merged request lists one signed PDF instead of one per upload. */
  documentMergedNote: {
    ...rs({
      padding: t(compact.documentRow.padding, documentRow.padding),
      fontSize: t(compact.documentRow.metaFontSize, documentRow.metaFontSize),
    }),
    color: documentRow.metaColor,
    lineHeight: 1.5,
  } as SxProps<Theme>,

  /** Below `md` the row itself is the preview target, so the button is dropped. */
  documentAction: {
    ...outlinedChrome,
    display: { xs: "none", md: "flex" },
    alignItems: "center",
    gap: documentRow.actionGap,
    minWidth: 0,
    height: documentRow.actionHeight,
    p: documentRow.actionPadding,
    borderRadius: documentRow.actionRadius,
    fontSize: documentRow.actionFontSize,
    fontWeight: documentRow.actionFontWeight,
    flexShrink: 0,
  } as SxProps<Theme>,

  documentIconAction: {
    ...outlinedChrome,
    ...rs({
      width: t(compact.documentRow.iconButtonSize, documentRow.iconButtonSize),
      height: t(compact.documentRow.iconButtonSize, documentRow.iconButtonSize),
      borderRadius: t(
        compact.documentRow.iconButtonRadius,
        documentRow.actionRadius,
      ),
    }),
    p: 0,
    flexShrink: 0,
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  documentThumbGlyph: {
    ...rs({
      width: t(compact.documentRow.thumbGlyphSize, documentRow.thumbGlyphSize),
      height: t(compact.documentRow.thumbGlyphSize, documentRow.thumbGlyphSize),
    }),
  } as SxProps<Theme>,

  documentIconGlyph: {
    ...rs({
      width: t(
        compact.documentRow.iconButtonGlyphSize,
        documentRow.iconButtonGlyphSize,
      ),
      height: t(
        compact.documentRow.iconButtonGlyphSize,
        documentRow.iconButtonGlyphSize,
      ),
    }),
  } as SxProps<Theme>,

  emptyRow: {
    ...rs({
      padding: t(compact.documentRow.padding, documentRow.padding),
      fontSize: t(compact.documentRow.metaFontSize, documentRow.metaFontSize),
    }),
    color: documentRow.metaColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  // ------------------------------------------------------------ level strip

  levelHeader: {
    display: "flex",
    alignItems: "center",
    ...rs({
      gap: t(compact.levelHeader.gap, levelHeader.gap),
      padding: t(compact.levelHeader.padding, levelHeader.padding),
    }),
  } as SxProps<Theme>,

  levelNum: (isCc: boolean) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      ...rs({
        minWidth: t(compact.levelHeader.numMinWidth, levelHeader.numMinWidth),
        height: t(compact.levelHeader.numHeight, levelHeader.numHeight),
        padding: t(compact.levelHeader.numPadding, levelHeader.numPadding),
        borderRadius: t(compact.levelHeader.numRadius, levelHeader.numRadius),
        fontSize: t(compact.levelHeader.numFontSize, levelHeader.numFontSize),
      }),
      bgcolor: isCc ? levelHeader.numCcBg : levelHeader.numBg,
      color: isCc ? levelHeader.numCcColor : levelHeader.numColor,
      fontWeight: levelHeader.numFontWeight,
      lineHeight: "normal",
      flexShrink: 0,
    }) as SxProps<Theme>,

  levelLabel: {
    ...rs({
      fontSize: t(compact.levelHeader.labelFontSize, levelHeader.labelFontSize),
    }),
    fontWeight: levelHeader.labelFontWeight,
    letterSpacing: levelHeader.labelLetterSpacing,
    color: levelHeader.labelColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** Like `levelRule`, the compact level strip stops at its label. */
  levelParallel: {
    display: { xs: "none", md: "block" },
    p: levelHeader.parallelPadding,
    borderRadius: levelHeader.parallelRadius,
    bgcolor: levelHeader.parallelBg,
    color: levelHeader.parallelColor,
    fontSize: levelHeader.parallelFontSize,
    fontWeight: levelHeader.parallelFontWeight,
    letterSpacing: levelHeader.parallelLetterSpacing,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    flexShrink: 0,
  } as SxProps<Theme>,

  /** The compact reference stops the level strip at its label. */
  levelRule: {
    display: { xs: "none", md: "block" },
    flex: 1,
    minWidth: 0,
    height: "1px",
    bgcolor: levelHeader.ruleColor,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- recipients

  recipientRow: (indented: boolean) =>
    ({
      display: "flex",
      ...rs({
        alignItems: ["flex-start", "flex-start", "flex-start", "center"],
        gap: t(compact.recipientRow.gap, recipientRow.gap),
        padding: indented
          ? t(compact.recipientRow.padding, recipientRow.padding)
          : t(compact.recipientRow.flatPadding, recipientRow.flatPadding),
        borderTop: [
          `1px solid ${card.divider}`,
          `1px solid ${card.divider}`,
          `1px solid ${card.divider}`,
          "none",
        ],
        borderBottom: ["none", "none", "none", `1px solid ${card.divider}`],
      }),
      "&:hover": { bgcolor: card.hoverBg },
    }) as SxProps<Theme>,

  recipientAvatar: (bg: string) =>
    ({
      ...rs({
        width: t(compact.recipientRow.avatarSize, recipientRow.avatarSize),
        height: t(compact.recipientRow.avatarSize, recipientRow.avatarSize),
        fontSize: t(
          compact.recipientRow.avatarFontSize,
          recipientRow.avatarFontSize,
        ),
      }),
      bgcolor: bg,
      color: recipientRow.avatarColor,
      fontWeight: recipientRow.avatarFontWeight,
      flexShrink: 0,
    }) as SxProps<Theme>,

  recipientNameRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    ...rs({ gap: t(compact.recipientRow.nameGap, recipientRow.nameGap) }),
    minWidth: 0,
  } as SxProps<Theme>,

  recipientName: {
    ...rs({
      fontSize: t(compact.recipientRow.nameFontSize, recipientRow.nameFontSize),
    }),
    fontWeight: recipientRow.nameFontWeight,
    color: recipientRow.nameColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  /** "CC" chip beside a copy-only recipient's name. */
  recipientRole: {
    ...rs({
      padding: t(compact.recipientRow.rolePadding, recipientRow.rolePadding),
      borderRadius: t(compact.recipientRow.roleRadius, recipientRow.roleRadius),
      fontSize: t(compact.recipientRow.roleFontSize, recipientRow.roleFontSize),
    }),
    bgcolor: recipientRow.roleCcBg,
    color: recipientRow.roleCcColor,
    fontWeight: recipientRow.roleFontWeight,
    letterSpacing: recipientRow.roleLetterSpacing,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    flexShrink: 0,
  } as SxProps<Theme>,

  recipientEmail: {
    ...rs({
      marginTop: t(
        compact.recipientRow.emailMarginTop,
        recipientRow.emailMarginTop,
      ),
      fontSize: t(
        compact.recipientRow.emailFontSize,
        recipientRow.emailFontSize,
      ),
    }),
    color: recipientRow.emailColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  recipientMetaRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: recipientRow.metaGap,
    ...rs({
      marginTop: t(
        compact.recipientRow.metaMarginTop,
        recipientRow.metaMarginTop,
      ),
      fontSize: t(compact.recipientRow.metaFontSize, recipientRow.metaFontSize),
    }),
    color: recipientRow.metaColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** The compact reference keeps only the timestamp on the meta line. */
  recipientMetaItem: {
    display: { xs: "none", md: "flex" },
    alignItems: "center",
    gap: "5px",
  } as SxProps<Theme>,

  recipientMetaIcon: {
    width: recipientRow.metaIconSize,
    height: recipientRow.metaIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  recipientNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: recipientRow.noteGap,
    ...rs({
      marginTop: t(
        compact.recipientRow.noteMarginTop,
        recipientRow.noteMarginTop,
      ),
      padding: t(compact.recipientRow.notePadding, recipientRow.notePadding),
      borderRadius: t(compact.recipientRow.noteRadius, recipientRow.noteRadius),
    }),
    bgcolor: recipientRow.noteBg,
    border: `1px solid ${recipientRow.noteBorder}`,
  } as SxProps<Theme>,

  recipientNoteIcon: {
    width: recipientRow.noteIconSize,
    height: recipientRow.noteIconSize,
    color: recipientRow.noteIconColor,
    flexShrink: 0,
    mt: "2px",
  } as SxProps<Theme>,

  recipientNoteText: {
    ...rs({
      fontSize: t(compact.recipientRow.noteFontSize, recipientRow.noteFontSize),
      lineHeight: [1.55, 1.55, 1.55, recipientRow.noteLineHeight],
    }),
    color: recipientRow.noteColor,
    wordBreak: "break-word",
  } as SxProps<Theme>,

  /** The compact reference has no per-row overflow menu. */
  recipientKebab: {
    display: { xs: "none", md: "inline-flex" },
    width: recipientRow.kebabSize,
    height: recipientRow.kebabSize,
    p: 0,
    borderRadius: recipientRow.kebabRadius,
    color: recipientRow.kebabColor,
    flexShrink: 0,
    "&:hover": {
      bgcolor: recipientRow.kebabHoverBg,
      color: recipientRow.kebabHoverColor,
    },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  // ----------------------------------------------------------------- message

  messageCard: {
    ...cardChrome,
    ...rs({
      borderRadius: cardRadiusTiers,
      padding: t(compact.message.padding, message.padding),
    }),
  } as SxProps<Theme>,

  messageTitle: {
    ...rs({
      marginBottom: t(
        compact.message.titleMarginBottom,
        message.titleMarginBottom,
      ),
      fontSize: t(compact.message.titleFontSize, message.titleFontSize),
    }),
    fontWeight: message.titleFontWeight,
    color: message.titleColor,
    lineHeight: "normal",
    marginTop: 0,
  } as SxProps<Theme>,

  messageBody: {
    ...rs({
      padding: t(compact.message.bodyPadding, message.bodyPadding),
      borderRadius: t(compact.message.bodyRadius, message.bodyRadius),
      fontSize: t(compact.message.bodyFontSize, message.bodyFontSize),
      lineHeight: t(compact.message.bodyLineHeight, message.bodyLineHeight),
    }),
    bgcolor: message.bodyBg,
    border: `1px solid ${message.bodyBorder}`,
    color: message.bodyColor,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  } as SxProps<Theme>,

  // ------------------------------------------------------------- audit trail

  auditHeader: {
    display: "flex",
    alignItems: "center",
    ...rs({
      gap: t(compact.audit.headerGap, audit.headerGap),
      padding: t(compact.audit.headerPadding, audit.headerPadding),
    }),
    borderBottom: `1px solid ${card.divider}`,
  } as SxProps<Theme>,

  auditIconTile: {
    ...rs({
      width: t(compact.audit.iconTileSize, audit.iconTileSize),
      height: t(compact.audit.iconTileSize, audit.iconTileSize),
    }),
    borderRadius: audit.iconTileRadius,
    bgcolor: audit.iconTileBg,
    color: audit.iconGlyphColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  auditIconGlyph: {
    ...rs({
      width: t(compact.audit.iconGlyphSize, audit.iconGlyphSize),
      height: t(compact.audit.iconGlyphSize, audit.iconGlyphSize),
    }),
  } as SxProps<Theme>,

  auditTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: audit.titleGap,
    minWidth: 0,
  } as SxProps<Theme>,

  auditTitle: {
    ...rs({
      fontSize: t(compact.audit.titleFontSize, audit.titleFontSize),
    }),
    fontWeight: audit.titleFontWeight,
    color: audit.titleColor,
    lineHeight: "normal",
    m: 0,
  } as SxProps<Theme>,

  /** The compact header is a single line — title, count, nothing else. */
  auditSub: {
    display: { xs: "none", md: "block" },
    mt: audit.subMarginTop,
    fontSize: audit.subFontSize,
    color: audit.subColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** Below `md` the page is the scroller, so the trail is never inset-scrolled. */
  auditBody: {
    ...rs({
      padding: t(compact.audit.bodyPadding, audit.bodyPadding),
      maxHeight: ["none", "none", "none", audit.bodyMaxHeight],
      overflow: ["visible", "visible", "visible", "auto"],
    }),
  } as SxProps<Theme>,

  auditEvent: {
    display: "flex",
    ...rs({ gap: t(compact.audit.eventGap, audit.eventGap) }),
  } as SxProps<Theme>,

  auditRail: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  auditRailDisc: (bg: string) =>
    ({
      ...rs({
        width: t(compact.audit.railSize, audit.railSize),
        height: t(compact.audit.railSize, audit.railSize),
      }),
      borderRadius: "50%",
      bgcolor: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      "& img": { display: "block" },
    }) as SxProps<Theme>,

  /** Inline "show the events hidden in between" trigger. */
  auditEllipsis: {
    ...rs({
      width: t(compact.audit.railSize, audit.railSize),
      height: t(compact.audit.railSize, audit.railSize),
    }),
    p: 0,
    borderRadius: "50%",
    bgcolor: audit.ellipsisBg,
    color: audit.ellipsisColor,
    flexShrink: 0,
    "&:hover": {
      bgcolor: audit.ellipsisBg,
      color: audit.iconGlyphColor,
    },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  auditConnector: {
    width: audit.connectorWidth,
    flex: 1,
    ...rs({
      minHeight: t(compact.audit.connectorMinHeight, audit.connectorMinHeight),
    }),
    bgcolor: audit.connectorColor,
  } as SxProps<Theme>,

  auditContent: {
    ...rs({
      paddingBottom: t(
        compact.audit.contentPaddingBottom,
        audit.contentPaddingBottom,
      ),
    }),
    minWidth: 0,
  } as SxProps<Theme>,

  auditActor: {
    ...rs({ fontSize: t(compact.audit.actorFontSize, audit.actorFontSize) }),
    fontWeight: audit.actorFontWeight,
    color: audit.actorColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  auditText: {
    mt: audit.textMarginTop,
    ...rs({
      fontSize: t(compact.audit.textFontSize, audit.textFontSize),
      lineHeight: t(compact.audit.textLineHeight, audit.textLineHeight),
    }),
    color: audit.textColor,
    wordBreak: "break-word",
  } as SxProps<Theme>,

  auditWhen: {
    ...rs({
      marginTop: t(compact.audit.whenMarginTop, audit.whenMarginTop),
      fontSize: t(compact.audit.whenFontSize, audit.whenFontSize),
    }),
    color: audit.whenColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  auditFooter: {
    ...rs({ padding: t(compact.audit.footerPadding, audit.footerPadding) }),
  } as SxProps<Theme>,

  auditFooterButton: {
    ...outlinedChrome,
    width: "100%",
    ...rs({
      height: t(compact.audit.footerButtonHeight, audit.footerButtonHeight),
      borderRadius: t(
        compact.audit.footerButtonRadius,
        audit.footerButtonRadius,
      ),
      fontSize: t(
        compact.audit.footerButtonFontSize,
        audit.footerButtonFontSize,
      ),
    }),
    fontWeight: audit.footerButtonFontWeight,
  } as SxProps<Theme>,

  /** Device an event came from: glyph + one line, truncated (full text in a tooltip). */
  auditDevice: {
    display: "flex",
    alignItems: "center",
    gap: audit.deviceGap,
    mt: audit.deviceMarginTop,
    minWidth: 0,
    color: audit.whenColor,
    "& svg": { fontSize: audit.deviceIconSize, flexShrink: 0 },
  } as SxProps<Theme>,

  auditDeviceText: {
    ...rs({ fontSize: t(compact.audit.whenFontSize, audit.whenFontSize) }),
    lineHeight: "normal",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /**
   * Tamper-evidence bar at the foot of the card: icon, title + detail, and a
   * compact action on the right. `tone` is the result's status-chip tone, or
   * undefined before the first check.
   */
  auditIntegrity: (tone?: keyof typeof audit.integrityTone) => {
    const palette = tone ? audit.integrityTone[tone] : undefined;
    return {
      ...rs({ margin: t(compact.audit.footerPadding, audit.footerPadding) }),
      display: "flex",
      alignItems: "center",
      gap: audit.integrityGap,
      padding: audit.integrityPadding,
      borderRadius: audit.integrityRadius,
      boxSizing: "border-box",
      bgcolor: palette ? palette.bg : audit.integrityIdle.bg,
      border: `1px solid ${palette ? "transparent" : audit.integrityIdle.border}`,
      "& .integrity-icon": {
        fontSize: audit.integrityIconSize,
        flexShrink: 0,
        color: palette ? palette.text : audit.integrityIdle.icon,
      },
      "& .integrity-title": {
        fontSize: audit.integrityTitleFontSize,
        fontWeight: audit.integrityTitleFontWeight,
        color: palette ? palette.text : audit.integrityIdle.text,
        lineHeight: "normal",
      },
      "& .integrity-action": {
        minWidth: 0,
        flexShrink: 0,
        px: 1,
        py: 0.5,
        fontSize: audit.integrityActionFontSize,
        fontWeight: audit.integrityActionFontWeight,
        color: palette ? palette.text : audit.integrityIdle.text,
        textTransform: "none",
      },
    } as SxProps<Theme>;
  },

  auditIntegrityText: {
    flex: 1,
    minWidth: 0,
  } as SxProps<Theme>,

  auditIntegrityDetail: {
    mt: audit.integrityDetailMarginTop,
    fontSize: audit.integrityDetailFontSize,
    color: audit.textColor,
    lineHeight: 1.4,
  } as SxProps<Theme>,

  // ---------------------------------------------------------- request details

  detailsCard: { ...cardChrome, p: details.padding } as SxProps<Theme>,

  detailsTitle: {
    mb: details.titleMarginBottom,
    fontSize: details.titleFontSize,
    fontWeight: details.titleFontWeight,
    color: details.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  detailsRows: {
    display: "flex",
    flexDirection: "column",
    gap: details.rowGap,
  } as SxProps<Theme>,

  detailsRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: details.fontSize,
  } as SxProps<Theme>,

  detailsLabel: {
    color: details.labelColor,
    fontWeight: details.labelFontWeight,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  detailsValue: (accent: boolean) =>
    ({
      color: accent ? details.valueAccentColor : details.valueColor,
      fontWeight: details.valueFontWeight,
      lineHeight: "normal",
      textAlign: "right",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }) as SxProps<Theme>,
};

export default viewSignRequestStyles;
